import React from 'react';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../core/LoadingSpinner';

interface MicrosoftSignInButtonProps {
  /**
   * Click handler for the button
   */
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Whether the button is in loading state
   */
  isLoading?: boolean;
  /**
   * Whether the button is disabled
   */
  disabled?: boolean;
  /**
   * Mode determines the button text: 'signin' or 'signup'
   */
  mode?: 'signin' | 'signup';
}

/**
 * MicrosoftSignInButton - Official Microsoft Sign-In button following brand guidelines
 *
 * Follows Microsoft identity platform branding guidelines:
 * - Light theme: white background
 * - 4-color Microsoft logo (red, green, blue, yellow squares)
 * - Text: "Sign in with Microsoft" or "Sign up with Microsoft"
 *
 * @see https://learn.microsoft.com/en-us/entra/identity-platform/howto-add-branding-in-apps
 */
const MicrosoftSignInButton: React.FC<MicrosoftSignInButtonProps> = ({
  onClick,
  isLoading = false,
  disabled = false,
  mode = 'signin',
}) => {
  const { t } = useTranslation();

  const buttonText = mode === 'signin'
    ? t('common:auth.signInWithMicrosoft', 'Sign in with Microsoft')
    : t('common:auth.signUpWithMicrosoft', 'Sign up with Microsoft');

  const loadingText = t('common:status.connecting', 'Connecting...');

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLoading || disabled}
      className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}
    >
      {isLoading ? (
        <>
          <LoadingSpinner size="sm" color="gray" />
          <span className="text-sm font-medium text-gray-700">
            {loadingText}
          </span>
        </>
      ) : (
        <>
          {/* Official Microsoft Logo - 4-color squares */}
          <svg
            width="18"
            height="18"
            viewBox="0 0 21 21"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            {/* Red square - top left */}
            <rect x="0" y="0" width="10" height="10" fill="#F25022" />
            {/* Green square - top right */}
            <rect x="11" y="0" width="10" height="10" fill="#7FBA00" />
            {/* Blue square - bottom left */}
            <rect x="0" y="11" width="10" height="10" fill="#00A4EF" />
            {/* Yellow square - bottom right */}
            <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
          </svg>
          <span className="text-sm font-medium text-gray-700">
            {buttonText}
          </span>
        </>
      )}
    </button>
  );
};

export default MicrosoftSignInButton;
